import axios from "axios";
import { unregisterFcmServiceWorker } from "@/AuthProvider";
import { REFRESH_TOKEN_REJECTED_STATUSES } from "@/constants/auth-constant";
import {
  getIsLoggingOut,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
} from "@/utils/auth-storage";
import { expireSession, isOnAuthRoute } from "@/utils/session-teardown";
import { getXtenantId } from "@/utils/utils";
import { AccessToken, UserControllerApi } from "./api";

type RefreshedTokens = { access_token?: string; refresh_token?: string };

type RefreshFailure = { isSessionExpired: boolean };

// Create an use a new instance of axios, separate from the one
// which has the interceptor installed.
// This avoids the deadlock where a failed refresh token request is
// indefinitely waiting for itself to complete.

const uninterceptedAxios = axios.create();
const authApi = new UserControllerApi(undefined, undefined, uninterceptedAxios);

// FIXME: Do we need mutual exclusion to control access to this?
let _currentRefreshPromise: Promise<string> | null = null;

async function refreshAuthToken(): Promise<string> {
  const reqBody: AccessToken = { refreshToken: getRefreshToken() };

  try {
    const res = await authApi.getAccessToken(reqBody, getXtenantId());
    const tokens = res?.data?.data as RefreshedTokens | undefined;

    if (!tokens?.access_token) {
      return Promise.reject<string>({ isSessionExpired: true });
    }
    // Keycloak rotates the refresh token on every refresh; keeping the original
    // one lets it expire on its own clock and ends the session mid-use.
    if (tokens.refresh_token) setRefreshToken(tokens.refresh_token);

    return tokens.access_token;
  } catch (refreshTokenError) {
    console.error(refreshTokenError);
    const status = axios.isAxiosError(refreshTokenError)
      ? refreshTokenError.response?.status
      : undefined;
    const failure: RefreshFailure = {
      isSessionExpired:
        status !== undefined &&
        REFRESH_TOKEN_REJECTED_STATUSES.includes(status),
    };
    return Promise.reject<string>(failure);
  }
}

async function refreshTokenInterceptor(error: any) {
  if (window.location.pathname?.split("/")?.[1] === "public")
    return Promise.resolve();
  else if (error.response?.status !== 401) {
    return Promise.reject(error);
  }

  // Nothing left to refresh with: the user is signing out, is already signed
  // out, or is sitting on an auth screen. Fail the request quietly instead of
  // tearing the session down again and bouncing to login a second time.
  if (getIsLoggingOut() || !getRefreshToken() || isOnAuthRoute()) {
    return Promise.reject(error);
  }

  let token: string;

  const currentRefreshPromise = _currentRefreshPromise;

  if (currentRefreshPromise !== null) {
    // We arrived here while some other request is already trying to refresh
    // the access token. We wait for that attempt to complete instead of
    // launching our own.

    token = await currentRefreshPromise.catch(
      (_) => Promise.reject(error) // Enure that we finish with the original error
    );
  } else {
    // Set up a new token refresh request if one has not been created yet
    // Take the ownership of this request, i.e. being responsible for clearing the
    // `currentRefreshPromise` when done

    try {
      const currentRefreshPromise = refreshAuthToken();
      _currentRefreshPromise = currentRefreshPromise;

      token = await currentRefreshPromise.catch(
        async (failure: RefreshFailure) => {
          // Only a rejected refresh token ends the session; a network blip or
          // a server error leaves it intact so the next 401 can retry.
          if (failure.isSessionExpired) {
            expireSession();
            await unregisterFcmServiceWorker();
          }
          return Promise.reject(error); // Enure that we finish with the original error
        }
      );
    } finally {
      // Regardless we succeeded or fail, the promise must be
      // Nulled out at the end so that a new token refresh request
      // is placed next time the token expires.
      _currentRefreshPromise = null;
    }
  }

  setAccessToken(token);

  const originalRequest = error.config;

  // Make sure not to retry the request if we have already done that before
  // To avoid infinite recursion
  if (originalRequest._retried) {
    return Promise.reject(error);
  }

  originalRequest._retried = true;
  originalRequest.headers["Authorization"] = "Bearer " + token;

  // Re-request
  return Promise.resolve(axios(error.config));
}

export function globallyInstallRefreshTokenInterceptor() {
  axios.interceptors.response.use(null, refreshTokenInterceptor);
}
